import { Section, Step, Tag, Note, Warn } from './parts'

export const SECTION_LABELS = [
  { id: 'overview', label: 'Overview' },
  { id: 'start',    label: 'Getting started' },
  { id: 'upload',   label: 'Registering data' },
  { id: 'merge',    label: 'Merging tilesets' },
  { id: 'formats',  label: 'Supported file formats' },
  { id: 'crs',      label: 'Coordinate systems (CRS)' },
  { id: 'viewer',   label: '3D viewer' },
  { id: 'style',    label: 'Point cloud styling' },
  { id: 'measure',  label: 'Measurement tools' },
  { id: 'manage',   label: 'Managing data' },
  { id: 'changedetection', label: 'Change detection' },
  { id: 'simulation', label: 'Flood simulation' },
  { id: 'account',  label: 'Account' },
]

function SimpleTable({ head, rows, mono }: { head: string[]; rows: string[][]; mono?: boolean }) {
  return (
    <div className="overflow-auto rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-100">
          <tr>
            {head.map(h => <th key={h} className="px-4 py-2 text-left font-semibold text-slate-700">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map(r => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`px-4 py-2 ${i === 0 ? (mono ? 'font-mono text-blue-700' : 'font-medium text-slate-800') + ' whitespace-nowrap' : 'text-slate-600'}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function ManualContentEn() {
  return (
    <>
      {/* 1. Overview */}
      <Section id="overview" title="1. Overview">
        <p>
          <strong>Mago 3D Reality Mesh Service</strong> converts urban mesh data into <strong>3D Tiles</strong> and
          visualizes it in 3D in a CesiumJS-based web viewer.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
          {[
            { icon: '📤', title: 'Register data', desc: 'Upload various 3D/spatial files and they are converted to 3D Tiles automatically.' },
            { icon: '🧩', title: 'Merge tilesets', desc: 'Combine several datasets into one tileset and view them together.' },
            { icon: '🌍', title: '3D visualization', desc: 'Check the converted tiles right away in the CesiumJS globe viewer.' },
            { icon: '📐', title: 'Spatial measurement', desc: 'Measure distance, area and volume directly in 3D space.' },
          ].map(c => (
            <div key={c.title} className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
              <div className="text-3xl mb-2">{c.icon}</div>
              <div className="font-semibold text-slate-800 text-sm mb-1">{c.title}</div>
              <div className="text-xs text-slate-500">{c.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* 2. Getting started */}
      <Section id="start" title="2. Getting started">
        <h3 className="font-semibold text-slate-800">Sign up</h3>
        <div className="space-y-2">
          <Step num={1}>On the login screen, click the <strong>Sign up</strong> link.</Step>
          <Step num={2}>Enter a <strong>username</strong>, then click <strong>Check availability</strong> to see whether it can be used.</Step>
          <Step num={3}>Enter your email, name and password (at least 8 characters) and complete the <strong>sign-up</strong>.</Step>
        </div>
        <Note>You must complete the username availability check before you can sign up.</Note>

        <h3 className="font-semibold text-slate-800 mt-6">Log in</h3>
        <div className="space-y-2">
          <Step num={1}>Enter your username and password, then click <strong>Log in</strong>.</Step>
          <Step num={2}>You can also sign in with the <strong>Log in with Naver</strong> button.</Step>
        </div>
        <Warn>Your login session expires automatically after <strong>1 hour</strong>. If it expires while you are working, you are logged out automatically.</Warn>
      </Section>

      {/* 3. Registering data */}
      <Section id="upload" title="3. Registering data">
        <p>Click the <strong>+ Register</strong> button at the top of the sidebar and choose one of the four methods below.</p>

        <div className="grid grid-cols-2 gap-3 mt-2">
          {[
            ['Quick registration', 'Register quickly with just a file and basic details. The coordinate system and output options are recommended automatically.'],
            ['Advanced registration', 'Set every mago-3d-tiler conversion option (LOD, Proj4, offsets, etc.) yourself in four steps.'],
            ['Merge existing tilesets', 'Merge several already-converted datasets into one tileset without reconverting.'],
            ['Merge new tilesets', 'Convert several unconverted source files one after another, then merge them into one.'],
          ].map(([t, d]) => (
            <div key={t} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-slate-800">{t}</p>
              <p className="text-xs text-slate-500 mt-1">{d}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400">For details on the two merge methods, see <strong>"4. Merging tilesets"</strong> below.</p>

        <h3 className="font-semibold text-slate-800 mt-6">Quick registration</h3>
        <div className="space-y-2">
          <Step num={1}>
            Drag and drop a file or click to select one. Switch to <strong>Folder upload</strong> to compress a whole
            folder automatically and register it as a single dataset (for example, a dataset made of several files such as OBJ + MTL + textures).
          </Step>
          <Step num={2}>Enter a name and description for the data.</Step>
          <Step num={3}>Select the coordinate reference system (CRS). Typical Korean data uses <strong>EPSG:5186</strong>.</Step>
          <Step num={4}>Click <strong>Register</strong> and the upload and conversion start automatically.</Step>
        </div>

        <h3 className="font-semibold text-slate-800 mt-6">Advanced registration</h3>
        <p className="text-sm">
          You can specify detailed conversion options such as the input format, output format and 3D Tiles version. In the
          last step you can preview the mago-3d-tiler CLI command that will actually run. Folder upload is supported, just like in quick registration.
        </p>
        <SimpleTable
          head={['Option', 'Description']}
          rows={[
            ['Input format', 'Set the file format manually. With auto-detect, it is inferred from the extension.'],
            ['Output format', 'Choose among B3DM (general 3D), I3DM (instanced) and PNTS (point cloud).'],
            ['3D Tiles version', 'Choose 1.0 or 1.1. 1.1 is recommended for compatibility with recent viewers.'],
            ['Coordinate system', 'Specify the original coordinate system of the data.'],
            ['LOD settings', 'Specify the minimum and maximum Level of Detail depth.'],
            ['Coordinate offset / origin', 'Specify X·Y·Z offsets, or the geographic origin (longitude, latitude) of local data.'],
          ]}
        />

        <Note>When the conversion finishes, a notification is sent to the email address you entered at registration.</Note>
        <Note>While converting, the status shows <strong>Converting</strong>, and it changes to <strong>Completed</strong> afterwards.</Note>
      </Section>

      {/* 4. Merging tilesets */}
      <Section id="merge" title="4. Merging tilesets">
        <p>
          Use this when you want to combine several datasets converted at different locations or times into one tileset.
          The merged result is added to the list as new data, separate from the originals, which stay as they are.
        </p>

        <h3 className="font-semibold text-slate-800 mt-4">Merge existing tilesets</h3>
        <p className="text-sm">Pick several datasets that are already <strong>Completed</strong> and combine them into one tileset immediately, without reconverting.</p>
        <div className="space-y-2">
          <Step num={1}>Select <strong>+ Register</strong> → <strong>Merge existing tilesets</strong>.</Step>
          <Step num={2}>Enter a name for the merged result and tick at least two converted datasets.</Step>
          <Step num={3}>Click <strong>Merge</strong>. It finishes immediately with no reconversion.</Step>
        </div>
        <Note>
          If an original dataset is deleted later, its part disappears from the merged result too. Conversely, if you apply a height
          adjustment to an original, it is reflected in the merged result automatically (the merge references the originals live).
        </Note>

        <h3 className="font-semibold text-slate-800 mt-6">Merge new tilesets</h3>
        <p className="text-sm">
          Upload several unconverted source files, convert each to 3D Tiles one after another, then combine them into one tileset
          with mago-3d-tiler's own merge feature.
        </p>
        <div className="space-y-2">
          <Step num={1}>Select <strong>+ Register</strong> → <strong>Merge new tilesets</strong>.</Step>
          <Step num={2}>
            Enter a name for the merged result and add at least two sources with <strong>+ Add file</strong> or <strong>+ Add folder</strong>.
            As with folder upload in advanced registration, a selected folder is compressed and added as a single source.
          </Step>
          <Step num={3}>
            If needed, expand <strong>Coordinate settings</strong> on each source to set a different CRS, offset or geographic origin
            per source (useful when merging data from different origins).
          </Step>
          <Step num={4}>Set the common options applied to the whole result, such as output format, 3D Tiles version and LOD, then click <strong>Start conversion and merge</strong>.</Step>
        </div>
        <Note>After the sources are uploaded, the server converts and merges them in sequence and emails you when done. The data does not appear in the list until conversion starts.</Note>
        <Warn>If even one source fails to convert, the whole merge fails (partial merge results are not saved).</Warn>
      </Section>

      {/* 5. Supported file formats */}
      <Section id="formats" title="5. Supported file formats">
        <SimpleTable
          mono
          head={['Extension', 'Description']}
          rows={[
            ['.obj',         'Wavefront OBJ — 3D mesh'],
            ['.ply',         'Stanford PLY — point cloud / mesh'],
            ['.fbx',         'Autodesk FBX — 3D animation / mesh'],
            ['.las / .laz',  'LiDAR point cloud (compressed)'],
            ['.gltf / .glb', 'GL Transmission Format — 3D scene'],
            ['.dae',         'COLLADA DAE — 3D assets'],
            ['.ifc',         'Industry Foundation Classes — BIM data'],
            ['.gml',         'CityGML — city 3D models'],
            ['.shp',         'ESRI Shapefile — vector spatial data'],
            ['.geojson',     'GeoJSON — vector spatial data'],
            ['.gpkg',        'GeoPackage — spatial database'],
            ['.kml',         'Keyhole Markup Language'],
            ['.zip',         'ZIP archive of the formats above'],
          ]}
        />
        <Note>Data made of several files (for example .obj + .mtl + textures, or .shp + .dbf + .prj) is compressed automatically when you use folder upload. You can also compress it into a ZIP yourself and upload that.</Note>
      </Section>

      {/* 6. CRS */}
      <Section id="crs" title="6. Coordinate systems (CRS)">
        <p>You must specify the original coordinate system of the uploaded data exactly so that it appears in the right place on the 3D globe.</p>
        <SimpleTable
          mono
          head={['EPSG code', 'Name', 'Typical use']}
          rows={[
            ['EPSG:5186', 'Korea 2000 Central (TM Central Origin)', 'General Korean survey data (recommended default)'],
            ['EPSG:5179', 'Korea 2000 / UTM-K (GRS80)',            'NGII digital topographic maps'],
            ['EPSG:5174', 'Korean 1985 Central (Bessel)',          'Older Korean survey data'],
            ['EPSG:5187', 'Korea 2000 East (TM East Origin)',      'Survey data for the eastern region'],
            ['EPSG:32652', 'UTM Zone 52N',                         'International coordinate system for East Asia'],
            ['EPSG:3857', 'Web Mercator',                          'Google Maps, OpenStreetMap'],
            ['EPSG:4326', 'WGS 84 (lat/lon)',                      'GPS data based on latitude/longitude'],
            ['EPSG:4978', 'WGS84 ECEF',                            'Earth-centered 3D coordinates (aerial, satellite)'],
          ]}
        />
        <Warn>If you pick the wrong coordinate system, the data appears in the wrong place on the 3D globe.</Warn>
      </Section>

      {/* 7. 3D viewer */}
      <Section id="viewer" title="7. 3D viewer">
        <h3 className="font-semibold text-slate-800">Navigating the map</h3>
        <div className="mt-2">
          <SimpleTable
            head={['Action', 'Result']}
            rows={[
              ['Drag with left mouse button', 'Rotate the map'],
              ['Drag with right mouse button', 'Pan the map'],
              ['Mouse wheel', 'Zoom in / out'],
              ['Double-click', 'Move the camera to that point'],
              ['Ctrl + drag', 'Adjust the camera tilt'],
            ]}
          />
        </div>

        <h3 className="font-semibold text-slate-800 mt-6">Showing and hiding tilesets</h3>
        <div className="space-y-2">
          <Step num={1}>In the data list on the left, click an item whose status is <strong>Completed</strong>.</Step>
          <Step num={2}>The 3D tiles appear on the map and the camera moves to them automatically.</Step>
          <Step num={3}>Click the same item again to hide the tiles.</Step>
        </div>

        <h3 className="font-semibold text-slate-800 mt-6">Changing the basemap</h3>
        <p className="text-sm">Click the <strong>Basemap</strong> button at the top right of the map to change the V-World basemap type.</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {['Base', 'Gray', 'Night', 'Satellite'].map(t => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
      </Section>

      {/* 8. Point cloud styling */}
      <Section id="style" title="8. Point cloud styling">
        <p>
          For point clouds converted from LAS/LAZ, show the item in the viewer and use the controls that appear below it
          in the data list to adjust the color mode, point size and distance attenuation.
        </p>

        <h3 className="font-semibold text-slate-800 mt-4">Color modes</h3>
        <div className="mt-2">
          <SimpleTable
            head={['Mode', 'Description']}
            rows={[
              ['Original color (RGB)', 'Shows the actual colors recorded by the lidar scan as they are.'],
              ['By classification', 'Colors points by ASPRS classification code, such as ground, building, vegetation and water.'],
              ['Intensity (grayscale)', 'Shows laser return intensity as grayscale.'],
              ['Intensity (heatmap)', 'Shows return intensity in color bands (blue → green → yellow → red).'],
              ['By elevation', 'Shows a color gradient based on elevation.'],
            ]}
          />
        </div>
        <Note>While the classification, intensity heatmap or elevation mode is in use, a legend explaining the colors appears next to the measurement tools panel at the bottom left.</Note>

        <h3 className="font-semibold text-slate-800 mt-6">Point size and distance attenuation</h3>
        <p className="text-sm">
          Use the slider to set the point size (1–5 px). Turning on <strong>distance attenuation</strong> makes points shrink
          naturally with distance from the camera, which renders more smoothly.
        </p>

        <Warn>Point clouds do not support transparency (they are always opaque) because of how they are rendered. Mesh (B3DM/I3DM) data can use the transparency slider.</Warn>
      </Section>

      {/* 9. Measurement tools */}
      <Section id="measure" title="9. Measurement tools">
        <p>Spatial measurement is available in the <strong>Measurement tools</strong> panel at the bottom left of the map. Use it when at least one 3D tileset is active.</p>

        {[
          {
            title: 'Distance',
            steps: [
              'Click Distance in the measurement tools panel.',
              'Click the starting point on the map.',
              'Click the intermediate points in order.',
              'Press Finish to show the total distance.',
            ],
          },
          {
            title: 'Area',
            steps: [
              'Click Area in the measurement tools panel.',
              'Click the vertices on the map in order to draw a polygon.',
              'Press Finish to show the area.',
            ],
          },
          {
            title: 'Volume',
            steps: [
              'Click Volume in the measurement tools panel.',
              'Click the vertices of the area to measure.',
              'Press Finish, then enter the reference height.',
              'The volume is calculated against the 3D mesh.',
            ],
          },
        ].map(({ title, steps }) => (
          <div key={title} className="mt-4">
            <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
            <div className="space-y-1.5">
              {steps.map((s, i) => <Step key={i} num={i + 1}>{s}</Step>)}
            </div>
          </div>
        ))}

        <Note>To clear the measurements, click the <strong>Reset</strong> button.</Note>
      </Section>

      {/* 10. Managing data */}
      <Section id="manage" title="10. Managing data">
        <h3 className="font-semibold text-slate-800">Data list</h3>
        <p className="text-sm">Your data list appears in the sidebar on the right of the dashboard. You can filter by name with the search box at the top.</p>
        <div className="mt-2">
          <SimpleTable
            head={['Status', 'Meaning']}
            rows={[
              ['Pending',    'Waiting to be converted.'],
              ['Converting', 'Being converted to 3D Tiles.'],
              ['Completed',  'Conversion is done and the data can be viewed in the 3D viewer.'],
              ['Failed',     'The conversion failed. Check the file and coordinate system, then register again.'],
            ]}
          />
        </div>

        <h3 className="font-semibold text-slate-800 mt-6">Copy URL</h3>
        <p className="text-sm">
          Clicking <strong>Copy URL</strong> in the data list copies the tile URL, including an access token, to the clipboard.
          Use it to load the converted tiles directly in an external Cesium viewer or another tool.
        </p>

        <h3 className="font-semibold text-slate-800 mt-6">Download</h3>
        <p className="text-sm">Clicking <strong>Download</strong> in the data list downloads all the converted 3D Tiles as a ZIP file.</p>

        <h3 className="font-semibold text-slate-800 mt-6">Height (elevation) adjustment</h3>
        <p className="text-sm">
          If converted data does not line up with the terrain in height, you can move it up or down with the
          <strong> Adjust height</strong> button, with no need to convert it again from scratch.
        </p>
        <div className="space-y-2">
          <Step num={1}>Click <strong>Adjust height</strong> in the data list.</Step>
          <Step num={2}>Enter the value in meters (positive = up, negative = down).</Step>
          <Step num={3}>Click <strong>Apply</strong> and it takes effect immediately.</Step>
        </div>
        <Warn>This adjustment edits the stored tile data itself and is permanent. To undo it, apply the same value with the opposite sign.</Warn>

        <h3 className="font-semibold text-slate-800 mt-6">Transparency</h3>
        <p className="text-sm">Mesh (B3DM/I3DM) data shown in the viewer has a transparency slider under its list item to adjust opacity. Point clouds (LAS) are not supported.</p>

        <h3 className="font-semibold text-slate-800 mt-6">Delete</h3>
        <p className="text-sm">Clicking <strong>Delete</strong> in the data list permanently deletes the data and its converted files.</p>
        <Warn>Deleted data cannot be recovered. If the data is part of a "Merge existing tilesets" result, that part disappears from the merged result too.</Warn>
      </Section>

      {/* 11. Change detection */}
      <Section id="changedetection" title="11. Change detection">
        <p>
          Change detection converts data captured or scanned at the same place at different times into <strong>voxels</strong> and
          compares them to calculate, as volumes, which parts were <strong>added</strong> and which were <strong>removed</strong>.
          Use it to check construction progress, landfill and stockpiles, or terrain change. There is nothing new to upload: it uses
          <strong> data that has already been converted to 3D Tiles</strong>.
        </p>

        <h3 className="font-semibold text-slate-800 mt-4">Getting started</h3>
        <div className="space-y-2">
          <Step num={1}>Click the <strong>Change Detection</strong> tab at the top of the dashboard sidebar.</Step>
          <Step num={2}>Create a project with <strong>+ Add project</strong> by entering a name and description. A project groups the data of one place over time.</Step>
          <Step num={3}>
            With <strong>+ Add date</strong>, choose converted data, then enter the survey date and the data type (<strong>Point Cloud</strong> or
            <strong> 3D Mesh</strong>) to register an observation. Voxel generation starts automatically.
          </Step>
          <Step num={4}>Select observations whose voxels are <strong>Done</strong>, enter a change detection name and click <strong>Run change detection</strong>.</Step>
          <Step num={5}>When the analysis finishes, open an item in the <strong>Change detection results</strong> list to see its volumes and the map display.</Step>
        </div>
        <Note>A notification appears when voxel generation or change detection finishes. Click it to jump to the result.</Note>

        <h3 className="font-semibold text-slate-800 mt-6">Comparison modes</h3>
        <SimpleTable
          head={['Mode', 'Description']}
          rows={[
            ['A / B (select 2)', 'Compare two observations whose voxels are done. The earlier one is the reference (A) and the later one is the target (B).'],
            ['Full time series', 'Sorts all observations with finished voxels by time and calculates the change for every adjacent time pair.'],
          ]}
        />

        <h3 className="font-semibold text-slate-800 mt-6">Reading the results</h3>
        <p className="text-sm">For each time pair, the results show the volumes below. On the map, voxels are distinguished by color.</p>
        <div className="mt-2">
          <SimpleTable
            head={['Item', 'Meaning']}
            rows={[
              ['Added voxels', 'Only in B (newly appeared)'],
              ['Removed voxels', 'Only in A (lost)'],
              ['Retained voxels', 'In both A and B (including parts whose shape changed)'],
              ['Net change', 'Added volume − removed volume'],
            ]}
          />
        </div>
        <p className="text-sm">
          With the <strong>Compare</strong> tool you can view two time points <strong>blinking</strong> (alternating) or in a <strong>split view</strong> (A on
          the left, B on the right, with linked cameras). For time-series results, use the slider at the bottom of the map to pick a time pair or to overlay all of them.
        </p>

        <h3 className="font-semibold text-slate-800 mt-6">Analysis area</h3>
        <p className="text-sm">You can analyze only the area you want, or exclude a certain area. There are two settings, and you can use both together.</p>
        <ul className="list-disc pl-5 text-sm space-y-1">
          <li><strong>Voxel region</strong> (project setting): the range in which voxels are created. Draw it on the map or upload a GeoPackage (GPKG) file, and choose <strong>Include</strong> (process only inside) or <strong>Exclude</strong> (skip the inside).</li>
          <li><strong>Analysis area</strong> (per change detection): a range applied to that change detection only. Draw it or specify it with a GPKG.</li>
        </ul>
        <Warn>When you change the voxel region, you are asked whether to regenerate the voxels of the existing observations. Change detection results that are already finished keep the previous region.</Warn>

        <h3 className="font-semibold text-slate-800 mt-6">Reports</h3>
        <p className="text-sm">
          From a finished change detection result you can generate and download a <strong>PDF</strong> or <strong>HWPX</strong> report.
          It includes an overview, a change summary, detailed volumes per time pair, a time-series chart and a map capture.
        </p>

        <Warn>Deleting a project or an observation also deletes its voxels and change detection results. The original data (3D Tiles) is not deleted.</Warn>
      </Section>

      {/* 12. Flood simulation */}
      <Section id="simulation" title="12. Flood simulation">
        <p>
          Simulate water flow and flooding on a 3D mesh you have registered. The heights of <strong>your uploaded mesh</strong> are laid over the base terrain
          (when <strong>Apply DSM</strong> is on), so the results reflect buildings and structures. It is available on every plan.
        </p>

        <h3 className="font-semibold text-slate-800 mt-4">Getting started</h3>
        <div className="space-y-2">
          <Step num={1}>In the data list, click the <strong>💧 Simulation</strong> button on an item whose status is <strong>Completed</strong>.</Step>
          <Step num={2}>A dashed rectangle appears on the map. Set the simulation area <strong>inside</strong> this mesh's range.</Step>
          <Step num={3}>
            Follow the panel to set the analysis area and resolution, the calculation method and the water supply (inflow, rainfall,
            reservoir release and so on), then <strong>run</strong> it.
          </Step>
          <Step num={4}>While it runs you can pause, reset and change the speed. After it finishes, replay the result with the time playback.</Step>
          <Step num={5}>To finish, click <strong>End simulation</strong> on the card at the top left.</Step>
        </div>

        <h3 className="font-semibold text-slate-800 mt-6">Quick mode and Expert mode</h3>
        <p className="text-sm">
          Switch modes with the <strong>Quick / Expert</strong> toggle in the panel header. Both modes share the same cases.
        </p>
        <ul className="list-disc pl-5 text-sm space-y-1">
          <li><strong>Quick</strong>: guides you through five steps: location → analysis area and resolution → calculation method → reservoir shape and volume → run.</li>
          <li><strong>Expert</strong>: you handle every setting yourself, including area drawing, inflow points, structures, rainfall, legend and recording.</li>
        </ul>

        <h3 className="font-semibold text-slate-800 mt-6">Saving and loading results</h3>
        <p className="text-sm">
          In Expert mode, turn on recording, run the simulation and save the recording file; a window then asks whether to save it to the server.
          A saved result can be opened again with <strong>Load</strong> from the <strong>Saved results</strong> list of that mesh data.
        </p>
        <Note>The size of saved results counts toward your storage quota. A single file can be up to 256 MB.</Note>
        <Warn>Quick mode has no save feature. Ending the simulation discards the current case and its results, so use the result export (GeoJSON) first if you need it.</Warn>

        <h3 className="font-semibold text-slate-800 mt-6">Requirements and notes</h3>
        <ul className="list-disc pl-5 text-sm space-y-1">
          <li>You need a desktop-sized screen (768 px wide or more) and a browser that supports WebGL.</li>
          <li>If the area extends beyond the mesh, heights outside it come from the default terrain (public DEM), so accuracy is lower.</li>
          <li>For data with tiles that failed to load, 'Apply DSM' is off by default. With DSM off, the mesh is not reflected and only the base terrain is used, so convert the data again or contact an administrator.</li>
          <li>You cannot use the simulation at the same time as the measurement tools or change detection.</li>
        </ul>
      </Section>

      {/* 11. Account */}
      <Section id="account" title="13. Account">
        <h3 className="font-semibold text-slate-800">Viewing your account</h3>
        <p className="text-sm">
          Click <strong>My info</strong> at the top right of the header to see your account details such as username, name, email,
          organization and join date, along with your current plan and quota (conversion, storage and streaming capacity, and whether downloads are available).
        </p>

        <h3 className="font-semibold text-slate-800 mt-6">Log out</h3>
        <p className="text-sm">Click <strong>Log out</strong> at the top right of the header to log out immediately.</p>

        <h3 className="font-semibold text-slate-800 mt-6">Automatic logout</h3>
        <p className="text-sm">For security, you are logged out automatically <strong>1 hour</strong> after logging in. Log in again to keep using the service.</p>
      </Section>
    </>
  )
}
